<template>
	<div class="flex filter_table">
		<div class="flex flex_column w_100">
			<ag-item
				:rowData="rowData"
				:columnDefs="columnDefs"
				:masterDetail="true"
				:detailRowHeight='525'
				:detailCellRenderer="detailCellRenderer"
				:frameworkComponents="frameworkComponents"
				v-on:sortChanged="sortChanged"
				:loading-warning-text="loadingWarningText"
				:key="loadingWarningText"
				:with-button-group="false"
				:with-tabs="true"
			></ag-item>
			<Pagination
				:totalNum="totalNum"
				:pageSize="pageSize"
				:sizeChange="sizeChange"
				:currentChange="currentChange"
				:currentPage="page"
			></Pagination>
		</div>
		<InvestorReceivablePaymentsFilter @apply-filter="applyFilter"/>
	</div>
</template>
<script>
import {investorPaymentsReceivablesApi} from "@/common/api/investorPaymentsReceivablesApi";

import InvestorReceivablePaymentsFilter
	from "@/components/templates/investor/payments/receivables/InvestorReceivablePaymentsFilter";
import {enumDef} from "@/common/enumDefs";
import {asLocalizedDate, asMoneyAmount, asPercentage} from "@/components/templates/agGrid/formatter/valueFormatters";
import DetailCellRenderer from "@/components/templates/investor/payments/receivables/detailCellRenderer";
import AgItem from "@/components/templates/agGrid/AgItem";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";

export default {
	extends: PaginationParent,
	components: {
		AgItem,
		InvestorReceivablePaymentsFilter,
		Pagination
	},
	props: {
		accountId: { type: Number, default: -1 },
		accountsAssigned: { type: Boolean }
	},
	data() {
		return {
			detailCellRenderer: "myDetailCellRenderer",
			frameworkComponents: {
				myDetailCellRenderer: DetailCellRenderer
			},
			rowData: null,
			columnDefs: [],
			filters: [],
			sorting: [],
		};
	},
	computed: {
		loadingWarningText() {
			return !this.accountsAssigned ? this.$t("common.noAccountsAssigned") : null;
		}
	},
	beforeMount() {
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		getList() {
			// Early out if the account is not yet set, showing an empty table
			if (this.accountId < 0) {
				return;
			}

			let postData = {
				columns: [],
				filters: this.filters || [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting,
			};

			investorPaymentsReceivablesApi.inspection.list(this.accountId, postData)
				.then(res => {
					this.setTotalNum(res.recordsTotal);
					this.rowData = res.data;
					this.rowData.forEach(row => {
						if (row.buyerToSellerPaymentStatus !== enumDef.paymentStatus.Revoked) {
							row.index = ""; // Index has to be set to allow rows to expand
						}
					});
				})
				.catch(this.toast);
		},
		setColumnDefs() {
			this.columnDefs = [
				{
					field: "index",
					minWidth: 40,
					maxWidth: 40,
					cellRenderer: "agGroupCellRenderer",
				},
				{
					headerName: this.$t("common.type"),
					field: "receivableType",
					minWidth: 65,
					maxWidth: 80,
					sortable: true,
					cellRendererFramework: "EnumAsIcon",
					enumDef: enumDef.receivableType,
				},
				{
					headerName: this.$t("common.invoiceNumber"),
					field: "invoiceNumber",
					minWidth: 175,
					sortable: true
				},
				{
					headerName: this.$t("common.contractNumber"),
					field: "installmentContractNumber",
					minWidth: 120,
					sortable: true
				},
				{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					nameType: "debtorName",
					idType: "debtorId",
					minWidth: 175,
					cellRendererFramework: "CompanyTem",
					sortable: true,
				},
				{
					headerName: this.$t("common.seller"),
					field: "sellerName",
					nameType: "sellerName",
					idType: "sellerId",
					minWidth: 175,
					cellRendererFramework: "CompanyTem",
					sortable: true,
				},
				{
					headerName: this.$t("common.seller.account.iban"),
					field: "buyerToSellerReceiverIban",
					minWidth: 180,
					sortable: true,
				},
				{
					headerName: this.$t("common.seller.account.bic"),
					field: "buyerToSellerReceiverBic",
					minWidth: 125,
					sortable: true,
				},
				{
					headerName: this.$t("investor.investments.table.header.boughtOn"),
					field: "assignmentDate",
					valueFormatter: asLocalizedDate(),
					minWidth: 120,
					sortable: true,
				},
				{
					headerName: this.$t("common.payoutAmount"),
					headerClass: "align-right",
					field: "settlementPrice",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 180,
					maxWidth: 180,
					valueFormatter: asMoneyAmount({
						currencyField: "currency"
					}),
					sortable: true
				},
				{
					headerName: this.$t("common.dueDate"),
					field: "dueDate",
					minWidth: 120,
					valueFormatter: asLocalizedDate(),
					cellRendererFramework: "OverdueRenderer",
					cellRendererParams: {
						auctionStatusField: "auctionStatus",
						paymentStatusField: "sellerToBuyerPaymentStatus"
					},
					sortable: true
				},
				{
					headerName: this.$t("common.retention"),
					headerClass: "align-right",
					field: "retention",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 100,
					maxWidth: 200,
					valueFormatter: asPercentage({ showEmptyAsNotAvailable: true, decimals: 0 }),
					sortable: true
				},
				{
					headerName: this.$t("common.amount"),
					headerClass: "align-right",
					field: "amount",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 125,
					maxWidth: 200,
					valueFormatter: asMoneyAmount({
						currencyField: "currency"
					}),
					sortable: true
				},
				{
					headerName: this.$t("common.outstanding"),
					headerComponentParams: {
						tooltip: this.$t("investor.payments.table.header.outstanding.tooltip")
					},
					headerClass: "align-right",
					field: "outstandingAmount",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 125,
					valueFormatter: asMoneyAmount({
						currencyField: "currency",
						percentValueField: "outstandingPercent"
					}),
					sortable: true
				},
				{
					headerName: this.$t("common.progress"),
					minWidth: 150,
					maxWidth: 150,
					cellRendererFramework: "InvestorReceivablePaymentsProgressStatus",
				},
			];
		},
		applyFilter(filters) {
			this.filters = filters;
			this.getList();
		}
	},
	watch: {
		accountId() {
			this.getList();
		}
	}
};
</script>

<style lang="scss" scoped>
@import "src/components/templates/investor/payments/receivables/investorReceivablePayments";
</style>
